import { type CalloutType } from '@factorialco/deprecated-design-system'
import {
  AllocationFragment,
  ExpensableBudgetFragment,
  IntegrationsSyncableStateFieldsFragment,
  SpendingAlertFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import { allocationStatusText, budgetStatusText } from 'modules/finance/lib/budgetTagInfo'

export type DuplicateExpenseAlert = SpendingAlertFragment & {
  source: NonNullable<SpendingAlertFragment['source']> & {
    __typename: 'ExpensesExpensable'
  }
  target: NonNullable<SpendingAlertFragment['target']> & {
    __typename: 'ExpensesExpensable'
  }
}

type AlertInfo = {
  message: string
  type: CalloutType
}

const checkAllocationAlert = (budgetAllocation?: AllocationFragment): AlertInfo | null => {
  if (!budgetAllocation?.status || budgetAllocation.status === 'within_allocation') {
    return null
  }

  return {
    message: allocationStatusText(budgetAllocation.status),
    type: budgetAllocation.status === 'over_allocation' ? 'critical' : 'warning',
  }
}

const checkBudgetAlert = (
  budget?: ExpensableBudgetFragment | null,
  allocationInBudget = true
): AlertInfo | null => {
  if (!budget || budget.currentStatus === 'within_budget') {
    return null
  }

  if (!allocationInBudget) {
    return {
      type: budget.currentStatus === 'over_budget' ? 'critical' : 'warning',
      message: '', // empty message, because we are not showing any snackbar
    }
  }

  return {
    type: budget.currentStatus === 'over_budget' ? 'critical' : 'warning',
    message: budgetStatusText(budget.currentStatus),
  }
}

const checkSpendingPolicyAlert = (
  spendingPolicy?: SpendingPolicesResultData,
  reimbursableAmount?: number,
  locale?: string
): AlertInfo | null => {
  if (spendingPolicy?.result !== 'out_of_policy') {
    return null
  }

  const policyData = spendingPolicy.policiesData[0]
  let message = i18n.t('spending_policies.status.out_of_policy')

  if (policyData && reimbursableAmount) {
    const category = policyData.category?.formatedValue
    const currency = policyData.currency?.formatedValue
    const allowedAmount = parseInt((policyData.amount?.formatedValue ?? '0').toString())
    const exceedeAmountCents = Math.max(0, reimbursableAmount - allowedAmount)

    if (category && exceedeAmountCents > 0 && currency && locale) {
      message = i18n.t('spending_policies.status.out_of_policy_with_details', {
        category,
        amount: formattedAmount({ amountCents: exceedeAmountCents, currency, locale }),
      })
    }
  }

  return {
    type: 'critical',
    message,
  }
}

const checkMileageAlert = (
  expensableType: 'expense' | 'mileage' | 'perdiem',
  mileageCalculatedMileage?: number,
  mileageMileage?: number,
  hasOtherAlerts = false
): AlertInfo | null => {
  if (expensableType !== 'mileage' || !mileageCalculatedMileage || mileageMileage == null) {
    return null
  }

  if (mileageCalculatedMileage === mileageMileage) {
    return null
  }

  const message = hasOtherAlerts
    ? i18n.t('expenses_v2.forms.fields.mileage.alert.multiple_alerts')
    : i18n.t('expenses_v2.forms.fields.mileage.alert.manually_changed.title')

  return {
    type: 'warning',
    message,
  }
}

const checkDuplicateExpenseAlert = (spendingAlerts?: SpendingAlertFragment[]): AlertInfo | null => {
  const duplicateAlerts = spendingAlerts?.filter((alert) => alert.type === 'duplicate_expense')

  if (duplicateAlerts && duplicateAlerts?.length > 0) {
    return {
      type: 'critical',
      message: i18n.t('expenses_v2.expense.duplicate_alerts.callout'),
    }
  }
  return null
}

const checkCategoryConflictAlert = (spendingAlerts?: SpendingAlertFragment[]): AlertInfo | null => {
  const categoryConflictAlerts = spendingAlerts?.filter(
    (alert) => alert.type === 'category_conflict'
  )

  if (categoryConflictAlerts && categoryConflictAlerts?.length > 0) {
    return {
      type: 'critical',
      message: i18n.t('expenses_v2.expense.category_conflict_alerts.callout'),
    }
  }
  return null
}

const checkPersistedSpendingPolicyAlert = (
  spendingAlerts: SpendingAlertFragment[]
): AlertInfo | null => {
  const workflowPolicyAlert = spendingAlerts?.find((alert) => alert.type == 'spending_policy')

  if (!workflowPolicyAlert) return null

  return {
    type: 'warning',
    message: workflowPolicyAlert.reason
      ? workflowPolicyAlert.reason
      : i18n.t('expenses_v2.expense.spending_alert.tooltip.spending_policy'),
  }
}

const checkSyncStatusAlert = (
  syncableState?: IntegrationsSyncableStateFieldsFragment | null
): AlertInfo | null => {
  if (!syncableState) return null

  const status = syncableState.status
  if (status !== 'failed' && status !== 'invalid') return null

  return {
    type: 'critical',
    message: i18n.t('expenses_v2.alerts.integrations.expense_not_synced'),
  }
}

const combineAlerts = (
  alerts: (AlertInfo | null)[]
): {
  alertCount: number
  alertMessage: string
  alertType: CalloutType
} => {
  const validSortedAlerts = alerts
    .filter((alert) => alert !== null)
    .sort((a, b) => (b.type === 'critical' ? 1 : -1) - (a.type === 'critical' ? 1 : -1))

  if (validSortedAlerts.length === 0) {
    return {
      alertCount: 0,
      alertMessage: '',
      alertType: 'warning',
    }
  }

  const hasCritical = validSortedAlerts.some((alert) => alert.type === 'critical')
  return {
    alertCount: validSortedAlerts.length,
    alertMessage: validSortedAlerts.find((alert) => alert.message)?.message || '',
    alertType: hasCritical ? 'critical' : 'warning',
  }
}

interface ExpenseAlertsParams {
  expensableType: 'expense' | 'mileage' | 'perdiem'
  isDraftManual: boolean
  expensableAmount: null | number
  reimbursableAmount: null | number
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicy?: SpendingPolicesResultData | null
  mileageRoundTrip?: boolean
  mileageCalculatedMileage?: number
  mileageMileage?: number
  spendingAlerts?: SpendingAlertFragment[]
  syncableState?: IntegrationsSyncableStateFieldsFragment | null
}

interface ExpenseAlertsReturn {
  alertCount: number
  alertMessage: string
  alertType: CalloutType
  hasDuplicateAlerts: boolean
}

export const useExpenseAlerts = ({
  expensableType,
  isDraftManual,
  expensableAmount,
  reimbursableAmount,
  budget,
  budgetAllocation,
  spendingPolicy,
  mileageRoundTrip: _mileageRoundTrip = false,
  mileageCalculatedMileage,
  mileageMileage,
  spendingAlerts,
  syncableState,
}: ExpenseAlertsParams): ExpenseAlertsReturn => {
  const {
    access: { locale },
  } = useCurrentSession()
  const allocationAlert =
    !isDraftManual && budgetAllocation ? checkAllocationAlert(budgetAllocation) : null
  const allocationInBudget = !allocationAlert || allocationAlert.type !== 'critical'
  const budgetAlert = !isDraftManual ? checkBudgetAlert(budget, allocationInBudget) : null

  const policyReimbursableAmount = reimbursableAmount || expensableAmount
  const policyAlert =
    policyReimbursableAmount && spendingPolicy
      ? checkSpendingPolicyAlert(
          spendingPolicy,
          parseInt(policyReimbursableAmount.toString()),
          locale
        )
      : null

  const persistedSpendingPolicyAlert = checkPersistedSpendingPolicyAlert(spendingAlerts ?? [])

  const duplicateAlert = checkDuplicateExpenseAlert(spendingAlerts)
  const categoryConflictAlert = checkCategoryConflictAlert(spendingAlerts)
  const syncAlert = checkSyncStatusAlert(syncableState)

  const hasOtherAlerts = !!(
    allocationAlert ||
    budgetAlert ||
    policyAlert ||
    duplicateAlert ||
    categoryConflictAlert ||
    persistedSpendingPolicyAlert ||
    syncAlert
  )
  const mileageAlert = checkMileageAlert(
    expensableType,
    mileageCalculatedMileage,
    mileageMileage,
    hasOtherAlerts
  )

  return {
    ...combineAlerts([
      duplicateAlert,
      categoryConflictAlert,
      policyAlert,
      allocationAlert,
      budgetAlert,
      mileageAlert,
      persistedSpendingPolicyAlert,
      syncAlert,
    ]),
    hasDuplicateAlerts: !!(duplicateAlert || categoryConflictAlert),
  }
}
